import {
  AudienceIcon,
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  SupportRadioIcon,
  SupportTvIcon,
  Typography,
  ValorationIcon,
} from '@acceso/design-system';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { ItemList } from '@/components/ItemList';
import { StatusSeenChipInTable } from '@/components/StatusSeenChipInTable';
import { UserAction } from '@/components/UserAction';
import { SEGMENT_REVIEW_STATES } from '@/constants';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { ChannelMediaTypeEnum } from '@/features/broadcasters/types';
import { TSegment } from '@/features/segments/types';
import { formatDateCustom } from '@/helpers/format-date';

type BroadcastScheduleProgramBodySegmentToolkitAditionalInfoDetailsProps = {
  item: TSegment | null;
};

export function BroadcastScheduleProgramBodySegmentToolkitAditionalInfoDetails({
  item,
}: BroadcastScheduleProgramBodySegmentToolkitAditionalInfoDetailsProps) {
  const { t } = useTranslation();
  const { data: { data: channels = [] } = {} } = useChannels();

  const selectedChannel = useMemo(
    () => channels?.find(channel => channel.id === item?.channel_id),
    [channels, item?.channel_id]
  );

  if (!item) return null;

  return (
    <div className="flex flex-1 flex-col items-start self-stretch">
      <div className="flex w-full flex-col divide-y divide-stroke-20">
        <ItemList title={t('Estado')}>
          <StatusSeenChipInTable
            review_state={item.review_state}
            reviewStates={SEGMENT_REVIEW_STATES}
          />
        </ItemList>
        <ItemList title={t('Emisora')}>
          <div className="flex items-center gap-1">
            <img
              src={selectedChannel?.parameters?.logo_presigned_url}
              alt="Logo"
              className="h-8 w-8 rounded-full object-contain"
            />
            <div className="flex flex-col gap-1">
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {selectedChannel?.name}
              </Typography>
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Regular}
                color={FontColorEnum.Medium}
              >
                {selectedChannel?.short_name}
              </Typography>
            </div>
          </div>
        </ItemList>
        <ItemList title={t('Programas')}>
          <Typography fontSize={FontSizeEnum.Md} color={FontColorEnum.Medium}>
            {item.programs.map(program => program.name).join(', ')}
          </Typography>
        </ItemList>
        <ItemList title={t('Fecha y hora')}>
          <div className="flex flex-col gap-1">
            <Typography
              fontSize={FontSizeEnum.Basic}
              weight={FontWeightEnum.Semibold}
              color={FontColorEnum.Dark10}
            >
              {formatDateCustom({ input: item.start_datetime, format: 'dd/MM/yyyy' })}
            </Typography>
            <Typography fontSize={FontSizeEnum.Basic} color={FontColorEnum.Dark10}>
              {formatDateCustom({ input: item.start_datetime, format: 'HH:mm' })} -
              {formatDateCustom({ input: item.end_datetime, format: 'HH:mm' })}
            </Typography>
          </div>
        </ItemList>
        <ItemList title={t('Soporte')}>
          {selectedChannel?.media_type.includes(ChannelMediaTypeEnum.TV) ? (
            <div className="flex items-center gap-1">
              <SupportTvIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {t('Televisión')}
              </Typography>
            </div>
          ) : (
            <div className="flex items-center gap-1">
              <SupportRadioIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {t('Radio')}
              </Typography>
            </div>
          )}
        </ItemList>
        <ItemList title={t('Datos')}>
          <div className="flex gap-2">
            <div className="flex items-center gap-1">
              <AudienceIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {item.audience || '-'} /{' '}
                {item.audience_rating_percent ? item.audience_rating_percent.toFixed(2) : '0.00'} %
              </Typography>
            </div>

            <div className="flex items-center gap-1">
              <ValorationIcon />
              <Typography
                fontSize={FontSizeEnum.Basic}
                weight={FontWeightEnum.Semibold}
                color={FontColorEnum.Dark10}
              >
                {item.economical_value || '-'}
              </Typography>
            </div>
          </div>
        </ItemList>
        <ItemList title={t('Actualizado por')}>
          <Typography fontSize={FontSizeEnum.Md} color={FontColorEnum.Medium}>
            <UserAction
              id={item.updated_by || item.created_by || ''}
              timestamp={item.updated_at || item.created_at}
            />
          </Typography>
        </ItemList>
      </div>
    </div>
  );
}
